import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building2, Users, BarChart3, Shield, Zap, Target, CheckCircle2 } from 'lucide-react';

const Enterprise = () => {
  const solutions = [
    {
      icon: Users,
      title: 'Employee Advocacy Programs',
      description: 'Turn your employees into brand ambassadors with coordinated LinkedIn strategies.'
    },
    {
      icon: Target,
      title: 'Corporate Branding',
      description: 'Establish your company as an industry leader with strategic content and positioning.'
    },
    {
      icon: BarChart3,
      title: 'Lead Generation at Scale',
      description: 'Enterprise-grade campaigns that generate qualified B2B leads consistently.'
    },
    {
      icon: Shield,
      title: 'Compliance & Security',
      description: 'Enterprise-level security with compliance monitoring and brand guidelines.'
    }
  ];

  const features = [
    'Multi-account management dashboard',
    'Dedicated account manager',
    'Custom training programs for teams',
    'Advanced analytics and reporting',
    'Content calendar and approval workflows',
    'Employee profile optimization',
    'C-suite executive positioning',
    'Competitor intelligence tracking',
    'API integration with CRM systems',
    '24/7 priority support',
    'Quarterly strategy reviews',
    'ROI tracking and optimization'
  ];

  const clients = [
    { company: 'Fortune 500', metric: '250K+', label: 'Employees Reached' },
    { company: 'Tech Unicorns', metric: '15M+', label: 'Impressions Generated' },
    { company: 'Global Enterprises', metric: '10K+', label: 'Leads Generated' }
  ];

  return (
    <div className="pt-16">
      <section className="relative py-32 bg-primary-dark text-text-inverse overflow-hidden" data-testid="enterprise-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(197, 160, 89, 0.3) 0%, transparent 50%)' }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4" data-testid="hero-subtitle">
              Enterprise Solutions
            </span>
            <h1 className="font-heading text-4xl lg:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title">
              LinkedIn Marketing for <span className="italic text-secondary-main">Global Enterprises</span>
            </h1>
            <p className="text-text-inverse/80 text-xl leading-relaxed mb-8" data-testid="hero-description">
              Scale your LinkedIn presence across your entire organization. From employee advocacy to executive positioning, we help enterprises dominate LinkedIn.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/enquiry"
                data-testid="hero-cta-primary"
                className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center transition-all"
              >
                Schedule Demo
              </Link>
              <a
                href="#solutions"
                data-testid="hero-cta-secondary"
                className="border border-text-inverse text-text-inverse hover:bg-white/10 rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center transition-all"
              >
                Explore Solutions
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white border-y border-primary-main/10" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {clients.map((client, index) => (
              <div key={index} data-testid={`client-stat-${index}`}>
                <div className="font-heading text-4xl font-bold text-primary-main mb-2">{client.metric}</div>
                <div className="text-text-secondary">{client.label}</div>
                <div className="text-xs text-text-muted mt-1">{client.company}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="solutions" className="py-20 bg-background-main" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-4" data-testid="solutions-title">
              Enterprise-Grade LinkedIn Solutions
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              Comprehensive solutions designed for organizations with complex needs and global reach.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-primary-main/10 p-8 hover:shadow-xl transition-all"
                  data-testid={`solution-card-${index}`}
                >
                  <Icon className="w-12 h-12 text-secondary-main mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-primary-main mb-3">{solution.title}</h3>
                  <p className="text-text-secondary leading-relaxed">{solution.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-4xl font-bold text-primary-main mb-6" data-testid="features-title">
                Everything Your Enterprise Needs
              </h2>
              <p className="text-text-secondary leading-relaxed mb-8">
                From small teams to Fortune 500 companies, our platform scales with your organization's needs.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Zap className="w-6 h-6 text-secondary-main flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-body font-semibold text-primary-main mb-1">Rapid Deployment</h4>
                    <p className="text-text-secondary text-sm">Launch enterprise-wide campaigns in days, not months.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Building2 className="w-6 h-6 text-secondary-main flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-body font-semibold text-primary-main mb-1">Multi-Location Support</h4>
                    <p className="text-text-secondary text-sm">Manage global teams across different time zones and markets.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Shield className="w-6 h-6 text-secondary-main flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-body font-semibold text-primary-main mb-1">Enterprise Security</h4>
                    <p className="text-text-secondary text-sm">SOC 2 compliant with advanced security features and controls.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-background-main p-8 border border-primary-main/10">
              <h3 className="font-heading text-2xl font-bold text-primary-main mb-6">Complete Feature List</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2" data-testid={`feature-${index}`}>
                    <CheckCircle2 className="w-4 h-4 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary-dark text-text-inverse">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Enterprise LinkedIn Presence?
          </h2>
          <p className="text-text-inverse/80 text-lg mb-8">
            Schedule a personalized demo with our enterprise team.
          </p>
          <Link
            to="/enquiry"
            className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
          >
            Contact Enterprise Sales
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Enterprise;